import type {
  JoinBatchItemDTO,
  JoinBatchPolicyDTO,
  GroupJoinPolicyDefaults,
  GroupJoinPolicyBounds,
  GroupJoinActorQuota,
  UserJoinPolicyInput,
  UserJoinPolicyPayload,
} from './kieu';

/**
 * Hiến pháp và tài liệu kiến trúc FBMax:
 * ARCH-01: Fat Server / Thin Client.
 * DATA-02: Không ngụy tạo thành công. UNKNOWN_RESULT không cho retry và không tự phát lại.
 * UI-01: Chống gửi chồng bằng ref lock, idempotency key ổn định khi mạng chưa xác nhận.
 * UI-04 / ARCH-01: Page tạm thời không hỗ trợ tham gia nhóm tự động theo chính sách an toàn.
 */

export const JOIN_LIMIT = 5;

export interface ItemStatusInfo {
  text: string;
  color: 'xanh' | 'xam' | 'vang' | 'do';
  description?: string;
}

export interface BatchStatusInfo {
  text: string;
  color: 'xanh' | 'xam' | 'vang' | 'do';
  description?: string;
}

/**
 * Ánh xạ trạng thái từng nhóm theo các enum thực tế trả về từ backend:
 * JOINED | REQUESTED_PENDING | NEEDS_USER_ACTION | UNKNOWN | UNKNOWN_RESULT |
 * UNAVAILABLE | RATE_LIMITED | CHECKPOINT | SESSION_EXPIRED |
 * QUEUED | RUNNING | SKIPPED | CANCELLED | PAUSED | FAILED
 */
export function getItemStatusInfo(status: string, outcomeCode?: string | null): ItemStatusInfo {
  const norm = String(outcomeCode || status || '').toUpperCase();

  switch (norm) {
    case 'JOINED':
      return {
        text: 'Đã tham gia',
        color: 'xanh',
        description: 'Đã vào nhóm công khai thành công',
      };
    case 'REQUESTED_PENDING':
      return {
        text: 'Chờ duyệt',
        color: 'vang',
        description: 'Đã gửi yêu cầu, đang chờ Quản trị viên phê duyệt',
      };
    case 'NEEDS_USER_ACTION':
      return {
        text: 'Cần trả lời câu hỏi',
        color: 'vang',
        description: 'Nhóm yêu cầu trả lời câu hỏi khảo sát trên Facebook (cần thao tác thủ công)',
      };
    case 'UNKNOWN':
    case 'UNKNOWN_RESULT':
      return {
        text: 'Chưa xác định',
        color: 'xam',
        description: 'Chưa nhận được bằng chứng có cấu trúc từ Facebook. Hệ thống không tự động thử lại.',
      };
    case 'UNAVAILABLE':
      return {
        text: 'Không khả dụng',
        color: 'do',
        description: 'Nhóm không tồn tại, bị lưu trữ hoặc bị hạn chế quyền truy cập',
      };
    case 'RATE_LIMITED':
      return {
        text: 'Bị giới hạn tần suất',
        color: 'do',
        description: 'Facebook tạm thời chặn thao tác tham gia nhóm do gửi quá nhanh',
      };
    case 'CHECKPOINT':
      return {
        text: 'Yêu cầu Checkpoint',
        color: 'do',
        description: 'Tài khoản gặp thử thách bảo mật từ Facebook, cần xác minh danh tính',
      };
    case 'SESSION_EXPIRED':
      return {
        text: 'Hết hạn phiên',
        color: 'do',
        description: 'Phiên đăng nhập Facebook đã hết hạn, cần mở tab Facebook để làm mới',
      };
    case 'QUEUED':
      return {
        text: 'Đang chờ',
        color: 'xam',
        description: 'Đang xếp hàng chờ tới lượt thực thi theo lịch giãn cách',
      };
    case 'RUNNING':
      return {
        text: 'Đang thực thi',
        color: 'xanh',
        description: 'Đang gửi mutation tham gia nhóm qua Extension',
      };
    case 'SKIPPED':
      return {
        text: 'Đã bỏ qua',
        color: 'xam',
        description: 'Bỏ qua vì tài khoản đã là thành viên hoặc đã gửi yêu cầu trước đó',
      };
    case 'CANCELLED':
      return {
        text: 'Đã hủy',
        color: 'xam',
        description: 'Yêu cầu đã được người dùng hủy khi còn đang trong hàng chờ',
      };
    case 'PAUSED':
      return {
        text: 'Tạm dừng',
        color: 'vang',
        description: 'Đợt tham gia đang tạm dừng, các yêu cầu chờ được giữ nguyên',
      };
    case 'FAILED':
    default:
      return {
        text: 'Thất bại',
        color: 'do',
        description: 'Không thể tham gia nhóm',
      };
  }
}

/**
 * Tuyệt đối không cho phép nút Retry cho trạng thái UNKNOWN theo quy tắc DATA-02.
 */
export function canRetryItem(status: string, outcomeCode?: string | null): boolean {
  const norm = String(outcomeCode || status || '').toUpperCase();
  if (norm === 'UNKNOWN' || norm === 'UNKNOWN_RESULT') {
    return false;
  }
  return false;
}

/**
 * Ánh xạ trạng thái đợt (batch status).
 */
export function getBatchStatusInfo(status: string): BatchStatusInfo {
  const norm = String(status || '').toUpperCase();

  switch (norm) {
    case 'ACTIVE':
      return { text: 'Đang chạy', color: 'xanh', description: 'Đang thực thi các nhóm theo tiến độ' };
    case 'PAUSED':
      return { text: 'Tạm dừng', color: 'vang', description: 'Đang tạm dừng theo yêu cầu của bạn' };
    case 'COMPLETED':
      return { text: 'Hoàn thành', color: 'xanh', description: 'Tất cả nhóm trong đợt đã được xử lý xong' };
    case 'CANCELLED':
      return { text: 'Đã hủy', color: 'xam', description: 'Đợt tham gia đã bị hủy các nhóm còn lại' };
    case 'BLOCKED_CHECKPOINT':
      return { text: 'Bị chặn (Checkpoint)', color: 'do', description: 'Tài khoản gặp checkpoint, đợt bị dừng an toàn' };
    case 'BLOCKED_RATE_LIMIT':
      return { text: 'Bị chặn (Tần suất)', color: 'do', description: 'Bị Meta giới hạn tần suất, đợt bị dừng an toàn' };
    case 'BLOCKED_SESSION':
      return { text: 'Bị chặn (Hết phiên)', color: 'do', description: 'Phiên đăng nhập Facebook hết hạn' };
    case 'BLOCKED_UNKNOWN':
      return { text: 'Bị chặn (Chưa rõ)', color: 'do', description: 'Phát hiện phản hồi chưa xác định, dừng để phòng ngừa checkpoint' };
    default:
      return { text: norm, color: 'xam' };
  }
}

/**
 * Hiển thị lịch hẹn thực thi `nextEligibleAt` từ server.
 * Chấp nhận cả chuỗi chuẩn ISO lẫn mốc mili-giây (epoch ms timestamp),
 * có lưu ý rõ đồng hồ client có thể lệch (clock drift) so với authority của server.
 */
export function formatScheduleTime(value: string | number | null | undefined): {
  formatted: string;
  hasSchedule: boolean;
  driftNotice: string;
} {
  const driftNotice = 'Lịch hẹn tính theo đồng hồ máy chủ (có thể lệch với đồng hồ thiết bị)';
  if (value === null || value === undefined || value === '') {
    return {
      formatted: 'Chưa có lịch hẹn',
      hasSchedule: false,
      driftNotice,
    };
  }

  try {
    let d: Date;
    if (typeof value === 'number') {
      d = new Date(value);
    } else if (typeof value === 'string') {
      const trimmed = value.trim();
      const num = Number(trimmed);
      if (!isNaN(num) && /^\d+$/.test(trimmed)) {
        d = new Date(num);
      } else {
        d = new Date(trimmed);
      }
    } else {
      d = new Date(value);
    }

    if (isNaN(d.getTime())) {
      return { formatted: String(value), hasSchedule: true, driftNotice };
    }
    const pad = (n: number) => String(n).padStart(2, '0');
    const hh = pad(d.getHours());
    const mm = pad(d.getMinutes());
    const ss = pad(d.getSeconds());
    const DD = pad(d.getDate());
    const MM = pad(d.getMonth() + 1);
    const YYYY = d.getFullYear();

    return {
      formatted: `${hh}:${mm}:${ss} ${DD}/${MM}/${YYYY}`,
      hasSchedule: true,
      driftNotice,
    };
  } catch {
    return {
      formatted: String(value),
      hasSchedule: true,
      driftNotice,
    };
  }
}

/**
 * Tóm tắt chính sách preset đã được duyệt (User approved):
 * - Giãn cách, quy mô hiệp, nghỉ giữa hiệp: hiển thị giá trị backend trả về, KHÔNG tự bịa ra nếu thiếu.
 * - Hạn mức ngày: CHỈ hiển thị nếu backend trả về, KHÔNG tự bịa ra nếu không có.
 */
export function getPresetPolicySummary(policy?: JoinBatchPolicyDTO): {
  hasPolicy: boolean;
  delayText: string;
  roundText: string;
  restText: string;
  quotaText: string | null;
} {
  const hasPolicy = Boolean(policy && (policy.delayMinSec || policy.roundSize || policy.roundRestSec || policy.dailyQuota));
  const minMin = policy?.delayMinSec ? Math.round(policy.delayMinSec / 60) : null;
  const maxMin = policy?.delayMaxSec ? Math.round(policy.delayMaxSec / 60) : null;
  const roundSize = policy?.roundSize || null;
  const restMin = policy?.roundRestSec ? Math.round(policy.roundRestSec / 60) : null;

  const delayText = (minMin !== null && maxMin !== null)
    ? `${minMin} – ${maxMin} phút/nhóm (ngẫu nhiên)`
    : (minMin !== null ? `${minMin} phút/nhóm` : 'Theo cài đặt máy chủ');

  const roundText = roundSize !== null
    ? `${roundSize} nhóm / hiệp`
    : 'Theo cài đặt máy chủ';

  const restText = restMin !== null
    ? `${restMin} phút giữa các hiệp`
    : 'Theo cài đặt máy chủ';

  let quotaText: string | null = null;
  if (typeof policy?.dailyQuota === 'number') {
    quotaText = typeof policy?.dailyMax === 'number'
      ? `${policy.dailyQuota} nhóm/ngày (Tối đa máy chủ: ${policy.dailyMax})`
      : `${policy.dailyQuota} nhóm/ngày`;
  }

  return {
    hasPolicy,
    delayText,
    roundText,
    restText,
    quotaText,
  };
}

export interface BatchProgressStats {
  total: number;
  completed: number;
  running: number;
  queued: number;
  skipped: number;
  failed: number;
  cancelled: number;
  paused: number;
  percent: number;
}

/**
 * Tính toán tiến độ đợt tham gia nhóm.
 */
export function calculateBatchProgress(items: JoinBatchItemDTO[] = []): BatchProgressStats {
  const total = items.length;
  if (total === 0) {
    return {
      total: 0,
      completed: 0,
      running: 0,
      queued: 0,
      skipped: 0,
      failed: 0,
      cancelled: 0,
      paused: 0,
      percent: 0,
    };
  }

  let completed = 0;
  let running = 0;
  let queued = 0;
  let skipped = 0;
  let failed = 0;
  let cancelled = 0;
  let paused = 0;

  for (const it of items) {
    const s = String(it.status || '').toUpperCase();
    if (s === 'JOINED' || s === 'REQUESTED_PENDING') {
      completed++;
    } else if (s === 'RUNNING') {
      running++;
    } else if (s === 'QUEUED') {
      queued++;
    } else if (s === 'SKIPPED') {
      skipped++;
    } else if (s === 'CANCELLED') {
      cancelled++;
    } else if (s === 'PAUSED') {
      paused++;
    } else {
      failed++;
    }
  }

  const finishedCount = completed + skipped + failed + cancelled;
  const percent = Math.min(100, Math.round((finishedCount / total) * 100));

  return {
    total,
    completed,
    running,
    queued,
    skipped,
    failed,
    cancelled,
    paused,
    percent,
  };
}

/**
 * Kiểm tra tính hợp lệ của yêu cầu tham gia nhóm trước khi gửi:
 * - Không gửi nếu danh sách rỗng hoặc vượt quá JOIN_LIMIT.
 * - Khóa nếu actor là Page: đưa ra lý do chính sách hệ thống rõ ràng, không tuyên bố Facebook platform không hỗ trợ.
 */
export function validateJoinSubmission({
  groupIds,
  isPage,
  joinLimit = JOIN_LIMIT,
}: {
  groupIds: string[];
  isPage?: boolean;
  joinLimit?: number;
}): { valid: boolean; reason?: string } {
  if (isPage) {
    return {
      valid: false,
      reason: 'Chức năng tham gia nhóm tự động hiện chỉ hỗ trợ tài khoản cá nhân, chưa hỗ trợ Fanpage (theo chính sách an toàn của hệ thống).',
    };
  }

  if (!groupIds || groupIds.length === 0) {
    return {
      valid: false,
      reason: 'Vui lòng chọn ít nhất 1 nhóm để gửi yêu cầu tham gia.',
    };
  }

  if (groupIds.length > joinLimit) {
    return {
      valid: false,
      reason: `Số nhóm vượt quá giới hạn cho phép (tối đa ${joinLimit} nhóm mỗi lần gửi).`,
    };
  }

  return { valid: true };
}

/**
 * Dịch các mã lỗi backend có cấu trúc sang tiếng Việt có nghĩa.
 */
export function translateJoinErrorCode(code: string | null | undefined, defaultMsg?: string | null): string {
  if (!code) return defaultMsg || 'Có lỗi xảy ra trong quá trình xử lý.';

  switch (code) {
    case 'PAGE_ACTOR_NOT_SUPPORTED':
      return 'Chức năng tham gia nhóm tự động chỉ hỗ trợ tài khoản cá nhân, không hỗ trợ Fanpage.';
    case 'CUSTOMER_QUOTA_INCREASE_REJECTED':
      return 'Hạn mức cá nhân không được vượt quá hạn mức máy chủ cho phép.';
    case 'INVALID_POLICY':
      return 'Tham số giãn cách hoặc hạn mức không hợp lệ hoặc nằm ngoài giới hạn cho phép của máy chủ.';
    case 'INVALID_POLICY_SETTINGS':
      return 'Cấu hình tham số chính sách quản trị không hợp lệ hoặc vi phạm giới hạn kỹ thuật tuyệt đối.';
    case 'IDEMPOTENCY_CONFLICT':
      return 'Yêu cầu bị xung đột: Trùng khóa gửi nhưng nội dung nhóm hoặc chính sách đã bị thay đổi.';
    case 'VERSION_CONFLICT':
      return 'Xung đột phiên bản chính sách: Cấu hình trên máy chủ đã thay đổi. Vui lòng tải lại trang.';
    case 'DAILY_QUOTA_EXCEEDED':
      return 'Tài khoản đã đạt hạn mức tham gia nhóm tối đa trong ngày.';
    case 'ACTOR_JOIN_BLOCKED':
      return 'Tài khoản đang bị hạn chế tham gia nhóm do phát hiện checkpoint hoặc sự cố trước đó.';
    case 'CLEARANCE_REQUIRED':
      return 'Không thể tiếp tục đợt tham gia: Tài khoản đang bị chặn checkpoint hoặc chưa rõ kết quả. Cần xác minh tài khoản trước.';
    case 'GROUP_JOIN_LIMIT_EXCEEDED':
      return `Chỉ cho phép gửi tối đa ${JOIN_LIMIT} nhóm mỗi lần gửi.`;
    case 'NO_ACTIVE_ACCOUNT':
      return 'Chưa nhận diện được tài khoản Facebook đang online. Vui lòng mở tab Facebook trên trình duyệt.';
    case 'ACTOR_NOT_VERIFIED':
      return 'Chưa xác minh được phiên kết nối online của tài khoản Facebook.';
    case 'ACTOR_TYPE_UNKNOWN':
      return 'Không thể xác định loại tài khoản (cá nhân hay Page). Cần xác minh tài khoản trước khi tham gia nhóm.';
    case 'ACTOR_MISMATCH':
      return 'Tài khoản gửi yêu cầu không khớp với tài khoản Facebook online.';
    case 'BATCH_NOT_FOUND':
      return 'Không tìm thấy thông tin đợt tham gia nhóm.';
    case 'INVALID_ACTION':
      return 'Thao tác điều khiển không hợp lệ.';
    default:
      return defaultMsg || `Lỗi máy chủ (${code})`;
  }
}

/**
 * Chuyển cấu hình mặc định từ máy chủ (đơn vị giây) sang dạng nhập liệu form người dùng (đơn vị phút).
 */
export function defaultPolicyToInput(defaults: GroupJoinPolicyDefaults): UserJoinPolicyInput {
  return {
    delayMinMinutes: Math.round(defaults.delayMinSec / 60),
    delayMaxMinutes: Math.round(defaults.delayMaxSec / 60),
    roundSize: defaults.roundSize,
    roundRestMinutes: Math.round(defaults.roundRestSec / 60),
    dailyLimit: defaults.dailyQuota,
  };
}

/**
 * Chuẩn hóa và tuần tự hóa dữ liệu nhập từ UI (phút) sang payload gửi lên backend (giây).
 * ARCH-01 / Fat Server: Giao diện chuyển đổi đơn vị chính xác, backend chịu trách nhiệm kiểm tra thẩm quyền.
 */
export function serializeUserJoinPolicy(input: UserJoinPolicyInput): UserJoinPolicyPayload {
  return {
    delayMinSec: Math.round(Number(input.delayMinMinutes) * 60),
    delayMaxSec: Math.round(Number(input.delayMaxMinutes) * 60),
    roundSize: Math.round(Number(input.roundSize)),
    roundRestSec: Math.round(Number(input.roundRestMinutes) * 60),
    dailyLimit: Math.round(Number(input.dailyLimit)),
  };
}

export interface PolicyValidationResult {
  valid: boolean;
  errors: Record<string, string>;
  firstError?: string;
}

/**
 * Kiểm tra hợp lệ chính sách người dùng trước khi gửi:
 * - Đảm bảo các giá trị nằm trong bounds từ server (không dùng hằng số cứng cố định).
 * - Kiểm tra delayMinMinutes <= delayMaxMinutes.
 * - Ràng buộc: dailyLimit <= serverDailyQuota (Customer Quota Constraint).
 */
export function validateUserJoinPolicy({
  input,
  bounds,
  serverDailyQuota,
}: {
  input: UserJoinPolicyInput;
  bounds?: GroupJoinPolicyBounds;
  serverDailyQuota?: number;
}): PolicyValidationResult {
  const errors: Record<string, string> = {};

  const minMinBounds = bounds ? Math.ceil(bounds.minDelaySec / 60) : 3;
  const maxMinBounds = bounds ? Math.floor(bounds.maxDelaySec / 60) : 10;
  const minRoundBounds = bounds ? bounds.minRoundSize : 1;
  const maxRoundBounds = bounds ? bounds.maxRoundSize : 3;
  const minRestBounds = bounds ? Math.ceil(bounds.minRoundRestSec / 60) : 30;
  const maxRestBounds = bounds ? Math.floor(bounds.maxRoundRestSec / 60) : 1440;
  const quotaCeiling = serverDailyQuota ?? 10;

  // delayMinMinutes
  if (input.delayMinMinutes === '' || isNaN(Number(input.delayMinMinutes))) {
    errors.delayMinMinutes = 'Vui lòng nhập giãn cách tối thiểu.';
  } else {
    const val = Number(input.delayMinMinutes);
    if (val < minMinBounds || val > maxMinBounds) {
      errors.delayMinMinutes = `Giãn cách tối thiểu phải từ ${minMinBounds} đến ${maxMinBounds} phút.`;
    }
  }

  // delayMaxMinutes
  if (input.delayMaxMinutes === '' || isNaN(Number(input.delayMaxMinutes))) {
    errors.delayMaxMinutes = 'Vui lòng nhập giãn cách tối đa.';
  } else {
    const val = Number(input.delayMaxMinutes);
    if (val < minMinBounds || val > maxMinBounds) {
      errors.delayMaxMinutes = `Giãn cách tối đa phải từ ${minMinBounds} đến ${maxMinBounds} phút.`;
    }
  }

  // delayMinMinutes <= delayMaxMinutes
  if (
    input.delayMinMinutes !== '' &&
    input.delayMaxMinutes !== '' &&
    !isNaN(Number(input.delayMinMinutes)) &&
    !isNaN(Number(input.delayMaxMinutes)) &&
    Number(input.delayMinMinutes) > Number(input.delayMaxMinutes)
  ) {
    errors.delayRange = 'Giãn cách tối thiểu không được lớn hơn giãn cách tối đa.';
  }

  // roundSize
  if (input.roundSize === '' || isNaN(Number(input.roundSize))) {
    errors.roundSize = 'Vui lòng nhập số nhóm mỗi hiệp.';
  } else {
    const val = Number(input.roundSize);
    if (!Number.isInteger(val) || val < minRoundBounds || val > maxRoundBounds) {
      errors.roundSize = `Số nhóm mỗi hiệp phải là số nguyên từ ${minRoundBounds} đến ${maxRoundBounds}.`;
    }
  }

  // roundRestMinutes
  if (input.roundRestMinutes === '' || isNaN(Number(input.roundRestMinutes))) {
    errors.roundRestMinutes = 'Vui lòng nhập thời gian nghỉ giữa các hiệp.';
  } else {
    const val = Number(input.roundRestMinutes);
    if (val < minRestBounds || val > maxRestBounds) {
      errors.roundRestMinutes = `Thời gian nghỉ giữa các hiệp phải từ ${minRestBounds} đến ${maxRestBounds} phút.`;
    }
  }

  // dailyLimit
  if (input.dailyLimit === '' || isNaN(Number(input.dailyLimit))) {
    errors.dailyLimit = 'Vui lòng nhập hạn mức tham gia trong ngày.';
  } else {
    const val = Number(input.dailyLimit);
    if (!Number.isInteger(val) || val < 1) {
      errors.dailyLimit = 'Hạn mức ngày phải là số nguyên lớn hơn hoặc bằng 1.';
    } else if (val > quotaCeiling) {
      errors.dailyLimit = `Hạn mức ngày không được vượt quá hạn mức máy chủ cho phép (${quotaCeiling} nhóm/ngày).`;
    }
  }

  const keys = Object.keys(errors);
  return {
    valid: keys.length === 0,
    errors,
    firstError: keys.length > 0 ? errors[keys[0]] : undefined,
  };
}

/**
 * Hiển thị tóm tắt xem trước chính sách hiệu lực kèm đơn vị, phiên bản snapshot và hạn mức đã biết.
 * Quota known only: Không tự suy đoán hoặc bịa số liệu khi server chưa trả về.
 */
export function formatPolicyPreview({
  input,
  version,
  actorQuota,
}: {
  input: UserJoinPolicyInput;
  version?: number;
  actorQuota?: GroupJoinActorQuota | null;
}): {
  versionText: string;
  delayText: string;
  roundText: string;
  restText: string;
  limitText: string;
  quotaSnapshot: {
    todayUsageText: string;
    remainingDailyText: string;
    dateKeyText: string;
    isBlocked: boolean;
    blockedReason: string | null;
  } | null;
} {
  const minMin = input.delayMinMinutes !== '' ? Number(input.delayMinMinutes) : null;
  const maxMin = input.delayMaxMinutes !== '' ? Number(input.delayMaxMinutes) : null;
  const roundSize = input.roundSize !== '' ? Number(input.roundSize) : null;
  const restMin = input.roundRestMinutes !== '' ? Number(input.roundRestMinutes) : null;
  const dailyLimit = input.dailyLimit !== '' ? Number(input.dailyLimit) : null;

  const versionText = typeof version === 'number' ? `Phiên bản v${version}` : 'Mặc định hiện hành';

  const delayText = (minMin !== null && maxMin !== null)
    ? `${minMin} – ${maxMin} phút/nhóm (ngẫu nhiên)`
    : (minMin !== null ? `${minMin} phút/nhóm` : 'Chưa thiết lập');

  const roundText = roundSize !== null ? `${roundSize} nhóm / hiệp` : 'Chưa thiết lập';
  const restText = restMin !== null ? `${restMin} phút giữa các hiệp` : 'Chưa thiết lập';
  const limitText = dailyLimit !== null ? `${dailyLimit} nhóm / ngày` : 'Theo hạn mức máy chủ';

  let quotaSnapshot = null;
  if (actorQuota && typeof actorQuota.todayUsage === 'number') {
    quotaSnapshot = {
      todayUsageText: `${actorQuota.todayUsage} nhóm`,
      remainingDailyText: `${actorQuota.remainingDaily} nhóm`,
      dateKeyText: actorQuota.dateKey || 'Hôm nay',
      isBlocked: Boolean(actorQuota.isBlocked),
      blockedReason: actorQuota.blockedReason || null,
    };
  }

  return {
    versionText,
    delayText,
    roundText,
    restText,
    limitText,
    quotaSnapshot,
  };
}
